import { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCurrency } from '../hooks/useCurrency'
import { useToast } from '../components/useToast'
import { useLanguage } from '../hooks/useLanguage'
import PlantillasRapidas from '../components/PlantillasRapidas'
import { usePageTour } from '../hooks/usePageTour'
import { getMovimientosSteps } from '../tours/movimientosTour'

// Categorías válidas — deben coincidir EXACTO con las que usa la IA en el Edge Function
const CATEGORIAS_INGRESO = [
  'Salario', 'Honorarios', 'Negocio propio', 'Horas extra', 'Comisiones',
  'Bonificaciones', 'Primas', 'Cesantías', 'Regalos o ayudas familiares', 'Otro'
]

const PAISES = {
  CO: { label: 'Colombia', emoji: '🇨🇴' },
  CA: { label: 'Canadá', emoji: '🇨🇦' },
  US: { label: 'Estados Unidos', emoji: '🇺🇸' }
}

// Tipos de archivo aceptados para el importador con IA
const EXTENSIONES_PERMITIDAS = ['.csv', '.txt', '.xml', '.pdf']
const MAX_SIZE_BYTES = 2 * 1024 * 1024 // 2 MB (los PDF pesan más que un CSV)

export default function Ingresos() {
  const { t } = useLanguage()
  const [ingresos, setIngresos] = useState([])
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [customCategory, setCustomCategory] = useState('')
  const location = useLocation()
  const fechaInicial = location.state?.fecha
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [filtroCategoriaPersonalizada, setFiltroCategoriaPersonalizada] = useState('')
  //hook
  const { format, currency, rates } = useCurrency()
  //toast
  const toast = useToast()

  // Importador con IA
  const csvRef = useRef(null)
  const [showImportPanel, setShowImportPanel] = useState(false)
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [csvPreview, setCsvPreview] = useState([])
  const [csvError, setCsvError] = useState('')
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)

  // Tutorial
  const iniciarTour = usePageTour(
    (t) => getMovimientosSteps({
      type: 'income', t,
      openImport: () => setShowImportPanel(true),
      closeImport: () => setShowImportPanel(false)
    }),
    () => setShowImportPanel(false)
  )

  // País (solo se usa como contexto para la IA, ej: formato de moneda/fecha)
  const [selectedCountry, setSelectedCountry] = useState(
    () => localStorage.getItem('miplata_csv_pais') || null
  )

  function elegirPais(codigo) {
    setSelectedCountry(codigo)
    localStorage.setItem('miplata_csv_pais', codigo)
  }

  //Formulario muestra la fecha
  const [date, setDate] = useState(
    fechaInicial || getTodayDate())

  // Función para fecha de HOY en formato YYYY-MM-DD
  function getTodayDate() {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')  // ✅ getDate()
    return `${year}-${month}-${day}`  // Ej: "2024-01-15"
  }
  function handleAmountChange(value) {
    // quitar todo lo que no sea número
    const limpio = value.replace(/\D/g, '')
    // formatear con puntos
    const formateado = new Intl.NumberFormat('es-CO').format(limpio)


    setForm(prev => ({
      ...prev,
      amount: formateado
    }))
  }
  const ingresosFiltrados = ingresos.filter((i) => {
    if (!filtroCategoria) return true
    if (filtroCategoria === '__personalizado__') {
      const texto = filtroCategoriaPersonalizada.trim().toLowerCase()
      if (!texto) return true
      return (i.category || '').toLowerCase().includes(texto)
    }
    return (i.category || '').toLowerCase() === filtroCategoria.toLowerCase()
  })

  // ──────────────────────────────────────────────────────────
  // IMPORTADOR CON IA (reemplaza la lógica manual por banco)
  // ──────────────────────────────────────────────────────────

  function detectarTipoArchivo(nombre) {
    const lower = nombre.toLowerCase()
    if (lower.endsWith('.pdf')) return 'pdf'
    if (lower.endsWith('.xml')) return 'xml'
    if (lower.endsWith('.csv')) return 'csv'
    if (lower.endsWith('.txt')) return 'txt'
    return null
  }

  function handleCsvFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setCsvError('')
    setCsvPreview([])

    // ── CAPA 1: extensión del archivo ──
    const nombreLower = file.name.toLowerCase()
    const extensionValida = EXTENSIONES_PERMITIDAS.some(ext => nombreLower.endsWith(ext))
    if (!extensionValida) {
      setCsvError('Solo se permiten archivos .csv, .txt, .xml o .pdf')
      setShowCsvModal(true)
      e.target.value = ''
      return
    }

    // ── CAPA 2: tipo MIME real (best-effort, algunos navegadores no lo reportan bien) ──
    const mimesPermitidos = [
      'text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel',
      'text/xml', 'application/xml', 'application/pdf', ''
    ]
    if (file.type && !mimesPermitidos.includes(file.type)) {
      setCsvError('El archivo no parece ser un formato válido.')
      setShowCsvModal(true)
      e.target.value = ''
      return
    }

    // ── CAPA 3: tamaño máximo ──
    if (file.size > MAX_SIZE_BYTES) {
      setCsvError(`El archivo es demasiado grande. Máximo permitido: ${MAX_SIZE_BYTES / (1024 * 1024)} MB.`)
      setShowCsvModal(true)
      e.target.value = ''
      return
    }

    const tipoArchivo = detectarTipoArchivo(file.name)
    procesarConIA(file, tipoArchivo)
    e.target.value = ''
  }

  async function procesarConIA(file, tipoArchivo) {
    setCsvLoading(true)
    setShowCsvModal(true)
    setCsvError('')

    try {
      let fileContent

      if (tipoArchivo === 'pdf') {
        // PDF → base64 puro (sin el prefijo data:...;base64,)
        fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result.split(',')[1])
          reader.onerror = () => reject(new Error('No se pudo leer el PDF'))
          reader.readAsDataURL(file)
        })
      } else {
        // CSV / TXT / XML → texto plano
        fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = () => reject(new Error('No se pudo leer el archivo'))
          reader.readAsText(file, 'UTF-8')
        })

        // Chequeo rápido en cliente: si viene vacío o parece binario, cortamos antes de gastar tokens
        if (!fileContent || typeof fileContent !== 'string' || fileContent.trim().length === 0) {
          throw new Error('El archivo está vacío o no se pudo leer como texto.')
        }
      }

      const { data, error } = await supabase.functions.invoke('parse-movimientos', {
        body: {
          fileType: tipoArchivo,
          fileContent,
          tipo: 'income',
          pais: selectedCountry
        }
      })

      if (error) {
        // supabase-js solo da "Edge Function returned a non-2xx status code"
        // por defecto — hay que leer el body real de la respuesta para saber qué pasó
        let mensaje = error.message || 'Error al analizar el documento'
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errBody = await error.context.json()
            if (errBody?.error) mensaje = errBody.error
          }
        } catch (_) {
          // si no se puede leer el body, nos quedamos con el mensaje genérico
        }
        throw new Error(mensaje)
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      const transacciones = Array.isArray(data?.transacciones) ? data.transacciones : []

      if (transacciones.length === 0) {
        setCsvError('No se detectaron ingresos en este documento. Verifica que sea el extracto correcto.')
        return
      }

      // Le damos a cada fila un id local para poder editarla/borrarla en la vista previa
      setCsvPreview(
        transacciones.map((row, idx) => ({
          _localId: idx,
          fecha: row.fecha,
          monto: row.monto,
          descripcion: row.descripcion || '',
          categoria: CATEGORIAS_INGRESO.includes(row.categoria) ? row.categoria : 'Otro'
        }))
      )
    } catch (err) {
      setCsvError(err.message || 'Error inesperado al analizar el documento.')
    } finally {
      setCsvLoading(false)
    }
  }

  function actualizarFilaPreview(localId, campo, valor) {
    setCsvPreview(prev =>
      prev.map(row => row._localId === localId ? { ...row, [campo]: valor } : row)
    )
  }

  function eliminarFilaPreview(localId) {
    setCsvPreview(prev => prev.filter(row => row._localId !== localId))
  }

  async function importarCsv() {
    if (csvPreview.length === 0) return
    setCsvImporting(true)

    try {
      const user = currentUser || await getUser()

      // Validación final antes de guardar (por si el usuario dejó algo inválido al editar)
      const filasValidas = csvPreview.filter(row => {
        const montoOk = row.monto && !isNaN(parseFloat(row.monto)) && parseFloat(row.monto) > 0
        const fechaOk = /^\d{4}-\d{2}-\d{2}$/.test(row.fecha)
        return montoOk && fechaOk
      })

      if (filasValidas.length === 0) {
        setCsvError('No hay filas válidas para importar. Revisa los montos y fechas.')
        return
      }

      // 1️⃣ Cifrar TODOS los registros primero
      const registros = await Promise.all(
        filasValidas.map(async (row) => {
          const { data: dCat } = await supabase.functions.invoke('secure-fields', {
            body: { action: 'encrypt', value: row.categoria || 'Otro' }
          })
          const { data: dDesc } = await supabase.functions.invoke('secure-fields', {
            body: { action: 'encrypt', value: row.descripcion || '' }
          })

          if (!dCat?.result) throw new Error('Error al cifrar categoría')

          return {
            user_id: user.id,
            type: 'income',
            amount: Math.round(parseFloat(row.monto)),
            category: dCat.result,
            description: dDesc?.result || null,
            date: row.fecha
          }
        })
      )

      // 2️⃣ Insertar TODO en una sola llamada
      const { error } = await supabase
        .from('transactions')
        .insert(registros)

      if (error) {
        setCsvError(`Error al importar: ${error.message}`)
        return
      }

      // 3️⃣ Solo si todo salió bien, limpiar y recargar
      setShowCsvModal(false)
      setCsvPreview([])
      fetchIngresos()

    } catch (err) {
      setCsvError('Error inesperado al importar. Intenta de nuevo.')
    } finally {
      setCsvImporting(false)
    }
  }

  const inputStyle = {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    fontSize: 14,
    outline: 'none',
    background: 'var(--color-bg)',
    color: 'var(--color-text)'
  }

  const today = new Date()

  const [selectedMonth, setSelectedMonth] = useState(
    String(today.getMonth() + 1).padStart(2, '0')
  )

  const [selectedYear, setSelectedYear] = useState(
    today.getFullYear()
  )

  const [form, setForm] = useState({
    amount: '',
    category: '',
    description: ''
  })

  const [error, setError] = useState('')

  const [editingId, setEditingId] = useState(null)
  const [editValue, setEditValue] = useState('')

  // 🔹 Obtener usuario actual
  async function getUser() {
    const { data } = await supabase.auth.getUser()
    return data.user
  }

  // 🔹 Obtener ingresos
  async function fetchIngresos() {
    setLoading(true)

    const user = await getUser()
    if (!user) { setLoading(false); return }
    setCurrentUser(user)

    const startDate = `${selectedYear}-${selectedMonth}-01`
    const endDate = new Date(selectedYear, selectedMonth, 0)
    const endDateString = `${selectedYear}-${selectedMonth}-${String(endDate.getDate()).padStart(2, '0')}`

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user.id)
      .eq('type', 'income')
      .gte('date', startDate)
      .lte('date', endDateString)
      .order('date', { ascending: false })

    if (error) { setLoading(false); return }

    const decryptedIngresos = await Promise.all(
      data.map(async (item) => {
        try {
          const [descResult, catResult] = await Promise.all([
            item.description
              ? supabase.functions.invoke('secure-fields', {
                body: { action: 'decrypt', value: item.description }
              })
              : Promise.resolve({ data: { result: '' } }),
            item.category
              ? supabase.functions.invoke('secure-fields', {
                body: { action: 'decrypt', value: item.category }
              })
              : Promise.resolve({ data: { result: '' } })
          ])
          return {
            ...item,
            description: descResult.data?.result ?? '',
            category: catResult.data?.result ?? ''
          }
        } catch {
          return {
            ...item,
            description: '(no disponible)',
            category: '(no disponible)'
          }
        }
      })
    )

    setIngresos(decryptedIngresos)
    setLoading(false)
  }

  useEffect(() => {
    fetchIngresos()
  }, [selectedMonth, selectedYear])

  useEffect(() => {
    const h = () => fetchIngresos()
    window.addEventListener('calypso:movimiento-agregado', h)
    return () => window.removeEventListener('calypso:movimiento-agregado', h)
  }, [selectedMonth, selectedYear])

  // 🔹 Manejo de inputs
  function handleChange(field) {
    return (e) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }))
    }
  }

  // 🔹 Agregar ingreso
  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    const limpio = form.amount.replace(/\./g, '')
    const amountEnMonedaUsuario = parseFloat(limpio)
    const rate = rates[currency] ?? 1
    const amount = Math.round(amountEnMonedaUsuario / rate) // convierte a COP para guardar

    if (!form.amount || isNaN(amount) || amount <= 0) {
      setError('El monto debe ser mayor a 0')
      return
    }
    if (!form.category) {
      setError('Selecciona una categoría')
      return
    }

    if (form.category === 'Otro' && customCategory.trim().length > 50) {
      setError('La categoría no puede tener más de 50 caracteres')
      return
    }
    if (form.description.trim().length > 200) {
      setError('La descripción no puede tener más de 200 caracteres')
      return
    }

    const user = currentUser || await getUser()
    if (!user) {
      setError('Sesión expirada. Recarga la página.')
      return
    }

    // 🔐 CIFRAR DESCRIPCIÓN
    let encryptedDescription = null

    if (form.description?.trim()) {

      const {
        data: encryptedData,
        error: encryptError
      } = await supabase.functions.invoke(
        'secure-fields',
        {
          body: {
            action: 'encrypt',
            value: form.description
          }
        }
      )

      if (encryptError) {
        setError('Error al cifrar descripción')
        return
      }

      encryptedDescription = encryptedData.result
    }

    // 🔐 CIFRAR CATEGORÍA
    const realCategory =
      form.category === 'Otro'
        ? customCategory
        : form.category

    let encryptedCategory = null

    if (realCategory?.trim()) {

      const {
        data: encryptedCategoryData,
        error: categoryEncryptError
      } = await supabase.functions.invoke(
        'secure-fields',
        {
          body: {
            action: 'encrypt',
            value: realCategory
          }
        }
      )

      if (categoryEncryptError) {
        setError('Error al cifrar categoría')
        return
      }

      encryptedCategory = encryptedCategoryData.result
    }

    const { error } = await supabase
      .from('transactions')
      .insert([{
        user_id: user.id,
        type: 'income',
        amount: amount,
        category: encryptedCategory,

        // 🔐 GUARDAR CIFRADO
        description: encryptedDescription,

        date: date
      }])

    if (error) {
      setError('Error al guardar')
      return
    }
    setDate("")

    // reset
    setForm({ amount: '', category: '', description: '' })
    setCustomCategory('')

    // recargar lista
    fetchIngresos()

    if (form.category === 'Otro' && !customCategory) {
      setError('Escribe una categoría personalizada')
      return
    }

    // reset
    setForm({ amount: '', category: '', description: '' })

    setForm({ amount: '', category: '', description: '' })
    setCustomCategory('')

    // recargar lista
    fetchIngresos()
  }

  // 🔹 Eliminar ingreso
  function handleDelete(id) {
    setConfirmDeleteId(id)
  }

  async function confirmarDelete() {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', confirmDeleteId)

    if (!error) {
      setConfirmDeleteId(null)
      fetchIngresos()
    }
  }

  // 🔹 Actualizar ingreso
  async function handleUpdate(id) {
    const { error } = await supabase
      .from('transactions')
      .update({ amount: parseFloat(editValue) })
      .eq('id', id)

    if (!error) {
      setEditingId(null)
      setEditValue('')
      fetchIngresos()
    }
  }


  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

      {/* Header */}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 20,
          fontWeight: 600
        }}>
          {t('ingresos_header')}
        </h2>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={iniciarTour}
            title={t('tour_ayuda')}
            style={{
              width: 30, height: 30, borderRadius: '50%',
              border: '1px solid var(--color-border)',
              background: 'transparent', color: 'var(--color-text)',
              fontWeight: 700, fontSize: 14, cursor: 'pointer'
            }}
          >
            ?
          </button>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'transparent',
              fontSize: 12,
              color: 'var(--color-text)'
            }}
          >
            {t('volver')}
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 640, margin: '0 auto', padding: 20 }}>

        {/* Total */}
        
        <div data-tour="mov-total" style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-md)',
          padding: 16,
          marginBottom: 20,
          border: '1px solid var(--color-border)'
        }}>

          {/* HEADER */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 10
          }}>

            <div>
              <div style={{
                fontSize: 11,
                color: '#19ba57',
                fontWeight: 700,
                textTransform: 'uppercase'
              }}>
                {t('total_ingresos')}
              </div>

              <div style={{
                fontSize: 14,
                color: '#19ba57',
                marginTop: 2
              }}>
                {new Date(selectedYear, selectedMonth - 1).toLocaleDateString(
                  'es-CO', { month: 'long', year: 'numeric' }
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>📅</span>
              <input
                data-tour="mov-mes"
                type="month"
                value={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-')
                  setSelectedYear(Number(year))
                  setSelectedMonth(month)
                }}
                style={{
                  border: '1px solid #19ba57',
                  borderRadius: 8,
                  padding: '4px 8px',
                  fontSize: 12,
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)'
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}>
            <div style={{
              fontSize: 26,
              fontWeight: 800,
              color: '#19ba57',
              fontFamily: 'var(--font-display)'
            }}>
              {format(ingresos.reduce((acc, i) => acc + i.amount, 0))}
            </div>

            <div
              onClick={() => navigate('/Analiticas')}
              style={{
                cursor: 'pointer',
                opacity: 0.85,
                transition: '0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.03)'
                e.currentTarget.style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.opacity = '0.85'
              }}
            >
              <svg width="120" height="35" viewBox="0 0 120 35" fill="none">
                <path
                  d="M0 25 L20 18 L40 20 L60 10 L80 12 L100 5 L120 8"
                  stroke="rgba(34,197,94,0.2)"
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  d="M0 25 L20 18 L40 20 L60 10 L80 12 L100 5 L120 8"
                  stroke="#4ade80"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                >
                  <animate
                    attributeName="d"
                    dur="2s"
                    repeatCount="indefinite"
                    values="
                  M0 25 L20 18 L40 20 L60 10 L80 12 L100 5 L120 8;
                  M0 22 L20 15 L40 17 L60 12 L80 10 L100 7 L120 5;
                  M0 25 L20 18 L40 20 L60 10 L80 12 L100 5 L120 8
                "
                  />
                </path>
              </svg>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form data-tour="mov-form" onSubmit={handleSubmit} style={{
          background: 'var(--color-surface)',
          padding: 16,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        }}>
          <input
            type="text"
            placeholder={t('monto_placeholder')}
            value={form.amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            style={inputStyle}
          />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              ...inputStyle,
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              padding: '12px 14px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              transition: '0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              cursor: 'pointer'
            }}
            onFocus={(e) => {
              e.target.style.border = '1px solid var(--color-primary)'
              e.target.style.boxShadow = '0 0 0 3px rgba(34,197,94,0.15)'
            }}
            onBlur={(e) => {
              e.target.style.border = '1px solid var(--color-border)'
              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
            }}
          />

          <select
            value={form.category}
            onChange={handleChange('category')}
            style={inputStyle}
          >
            <option value="">{t('selecciona_categoria')}</option>

            <optgroup label={t('cat_principales')}>
              <option value="Salario">{t('cat_salario')}</option>
              <option value="Honorarios">{t('cat_honorarios')}</option>
              <option value="Negocio propio">{t('cat_negocio_propio')}</option>
            </optgroup>

            <optgroup label={t('cat_adicionales')}>
              <option value="Horas extra">{t('cat_horas_extra')}</option>
              <option value="Comisiones">{t('cat_comisiones')}</option>
              <option value="Bonificaciones">{t('cat_bonificaciones')}</option>
            </optgroup>

            <optgroup label={t('cat_ocasionales')}>
              <option value="Primas">{t('cat_primas')}</option>
              <option value="Cesantías">{t('cat_cesantias')}</option>
              <option value="Regalos o ayudas familiares">
                {t('cat_regalos')}
              </option>
            </optgroup>

            <optgroup label={t('cat_personalizado')}>
              <option value="Otro">{t('cat_otro')}</option>
            </optgroup>
          </select>

          {form.category === 'Otro' && (
            <input
              type="text"
              placeholder={t('titulo_categoria_placeholder')}
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              style={inputStyle}
            />
          )}

          <input
            type="text"
            placeholder={t('descripcion_placeholder')}
            value={form.description}
            onChange={handleChange('description')}
            style={inputStyle}
          />

          <button style={{
            marginTop: 6,
            padding: '12px',
            background: 'var(--color-primary)',
            color: '#fff',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 600
          }}>
            {t('agregar_ingreso')}
          </button>

        </form>

        {/* Banner importar con IA */}
        <div data-tour="mov-importar" style={{
          background: 'var(--color-surface)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: '14px 16px',
          marginBottom: 20
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12, marginBottom: showImportPanel ? 14 : 0
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 3 }}>
                ¿Tienes tu extracto bancario? <span style={{ color: '#22c55e' }}>Súbelo y la IA lo lee por ti</span> 🤖
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                Acepta el archivo tal cual lo descargas del banco: CSV, TXT, XML o PDF
              </div>
            </div>
            <button
              onClick={() => setShowImportPanel(p => !p)}
              style={{
                flexShrink: 0, fontSize: 26,
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: 12, width: 52, height: 52,
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', transition: '0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(34,197,94,0.08)'}
              title="Importar con IA"
            >
              🧾
            </button>
          </div>

          {showImportPanel && (
            <div data-tour="mov-importar-panel">
              <div style={{ fontSize: 11, color: 'var(--color-muted)', marginBottom: 8, fontWeight: 600 }}>
                {t('selecciona_pais')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                {Object.entries(PAISES).map(([codigo, pais]) => (
                  <button
                    key={codigo}
                    onClick={() => elegirPais(codigo)}
                    style={{
                      padding: '12px 6px', borderRadius: 10, cursor: 'pointer',
                      border: selectedCountry === codigo
                        ? '2px solid #22c55e'
                        : '1px solid var(--color-border)',
                      background: selectedCountry === codigo ? 'rgba(34,197,94,0.08)' : 'transparent',
                      color: selectedCountry === codigo ? '#22c55e' : 'var(--color-text)',
                      fontWeight: 700, fontSize: 12, transition: '0.2s',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{pais.emoji}</span>
                    {pais.label}
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  if (!selectedCountry) { toast.warning('Primero selecciona tu país'); return }
                  csvRef.current?.click()
                }}
                style={{
                  width: '100%', padding: '11px', borderRadius: 10,
                  background: selectedCountry ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
                  border: selectedCountry
                    ? '1px solid rgba(34,197,94,0.25)'
                    : '1px solid var(--color-border)',
                  color: selectedCountry ? '#22c55e' : 'var(--color-muted)',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: '0.2s'
                }}
              >
                📂 {selectedCountry ? 'Subir extracto bancario' : 'Selecciona un país primero'}
              </button>

              <input
                ref={csvRef}
                type="file"
                accept=".csv,.txt,.xml,.pdf"
                onChange={handleCsvFile}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>
        

        {/* Modal previsualización IA */}
        {showCsvModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.6)', display: 'flex',
            justifyContent: 'center', alignItems: 'center',
            zIndex: 100, padding: 16, boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#111', padding: 20, borderRadius: 20,
              width: '100%', maxWidth: 460,
              display: 'flex', flexDirection: 'column', gap: 10,
              maxHeight: '85vh', overflowY: 'auto'
            }}>
              {csvLoading ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
                  🤖 Analizando el documento con IA...<br />
                  <span style={{ fontSize: 11 }}>Esto puede tardar unos segundos</span>
                </div>
              ) : csvError ? (
                <>
                  <div style={{ fontSize: 14, color: '#f87171', textAlign: 'center', padding: '16px 0' }}>
                    ⚠️ {csvError}
                  </div>
                  <button
                    onClick={() => { setShowCsvModal(false); setCsvError(''); setCsvPreview([]) }}
                    style={{
                      padding: 12, borderRadius: 10, background: '#333',
                      color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    {t('cerrar_modal')}
                  </button>
                </>
              ) : (
                <>
                  <h3 style={{ margin: 0, fontSize: 16, color: 'white' }}>
                    📄 Ingresos detectados ({csvPreview.length})
                  </h3>
                  <div style={{ fontSize: 12, color: '#aaa' }}>
                    Revisa, edita o elimina antes de importar
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
                    {csvPreview.map((row) => (
                      <div key={row._localId} style={{
                        padding: '10px 12px', borderRadius: 10,
                        background: 'rgba(34,197,94,0.06)',
                        border: '1px solid rgba(34,197,94,0.2)',
                        display: 'flex', flexDirection: 'column', gap: 6
                      }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="date"
                            value={row.fecha}
                            onChange={(e) => actualizarFilaPreview(row._localId, 'fecha', e.target.value)}
                            style={{ ...inputStyle, background: '#1a1a1a', color: '#fff', flex: 1, fontSize: 12, padding: '6px 8px' }}
                          />
                          <input
                            type="number"
                            value={row.monto}
                            onChange={(e) => actualizarFilaPreview(row._localId, 'monto', e.target.value)}
                            style={{ ...inputStyle, background: '#1a1a1a', color: '#22c55e', flex: 1, fontSize: 12, padding: '6px 8px', fontWeight: 700 }}
                          />
                          <button
                            onClick={() => eliminarFilaPreview(row._localId)}
                            style={{
                              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                              color: '#f87171', borderRadius: 8, padding: '0 10px', cursor: 'pointer'
                            }}
                            title="Eliminar esta fila"
                          >
                            🗑️
                          </button>
                        </div>
                        <input
                          type="text"
                          value={row.descripcion}
                          maxLength={100}
                          placeholder={t('sin_descripcion')}
                          onChange={(e) => actualizarFilaPreview(row._localId, 'descripcion', e.target.value)}
                          style={{ ...inputStyle, background: '#1a1a1a', color: '#fff', fontSize: 12, padding: '6px 8px' }}
                        />
                        <select
                          value={row.categoria}
                          onChange={(e) => actualizarFilaPreview(row._localId, 'categoria', e.target.value)}
                          style={{ ...inputStyle, background: '#1a1a1a', color: '#fff', fontSize: 12, padding: '6px 8px' }}
                        >
                          {CATEGORIAS_INGRESO.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={importarCsv}
                    disabled={csvImporting || csvPreview.length === 0}
                    style={{
                      padding: 12, borderRadius: 10,
                      background: csvImporting ? '#1a4d2e' : '#22c55e',
                      color: '#fff', border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: 14, opacity: (csvImporting || csvPreview.length === 0) ? 0.7 : 1
                    }}
                  >
                    {csvImporting ? t('importando') : `✅ Importar ${csvPreview.length} ingreso(s)`}
                  </button>

                  <button
                    onClick={() => { setShowCsvModal(false); setCsvPreview([]) }}
                    style={{
                      padding: 12, borderRadius: 10, background: '#333',
                      color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    {t('cancelar')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Filtro por categoría */}
        <div data-tour="mov-filtro" style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          padding: '12px 14px',
          marginBottom: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase' }}>
            {t('filtrar_categoria')}
          </div>

          <select
            value={filtroCategoria}
            onChange={(e) => {
              setFiltroCategoria(e.target.value)
              setFiltroCategoriaPersonalizada('')
            }}
            style={inputStyle}
          >
            <option value="">{t('todas_categorias')}</option>

            <optgroup label={t('cat_principales')}>
              <option value="Salario">{t('cat_salario')}</option>
              <option value="Honorarios">{t('cat_honorarios')}</option>
              <option value="Negocio propio">{t('cat_negocio_propio')}</option>
            </optgroup>

            <optgroup label={t('cat_adicionales')}>
              <option value="Horas extra">{t('cat_horas_extra')}</option>
              <option value="Comisiones">{t('cat_comisiones')}</option>
              <option value="Bonificaciones">{t('cat_bonificaciones')}</option>
            </optgroup>

            <optgroup label={t('cat_ocasionales')}>
              <option value="Primas">{t('cat_primas')}</option>
              <option value="Cesantías">{t('cat_cesantias')}</option>
              <option value="Regalos o ayudas familiares">{t('cat_regalos')}</option>
            </optgroup>

            <optgroup label={t('cat_personalizado')}>
              <option value="__personalizado__">{t('buscar_cat_personalizada')}</option>
            </optgroup>
          </select>

          {filtroCategoria === '__personalizado__' && (
            <input
              type="text"
              placeholder={t('escribir_nombre_cat')}
              value={filtroCategoriaPersonalizada}
              maxLength={50}
              onChange={(e) => setFiltroCategoriaPersonalizada(e.target.value)}
              style={inputStyle}
            />
          )}

          {filtroCategoria && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 11,
              color: 'var(--color-muted)'
            }}>
              <span>
                {ingresosFiltrados.length} {ingresosFiltrados.length !== 1 ? t('resultados') : t('resultado')}
                {filtroCategoria === '__personalizado__' && filtroCategoriaPersonalizada
                  ? ` ${t('para_texto')} "${filtroCategoriaPersonalizada}"`
                  : filtroCategoria !== '__personalizado__'
                    ? ` ${t('en_texto')} "${filtroCategoria}"`
                    : ''}
              </span>
              <button
                onClick={() => { setFiltroCategoria(''); setFiltroCategoriaPersonalizada('') }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600
                }}
              >
                {t('limpiar_filtro')}
              </button>
            </div>
          )}
        </div>

        {/* Lista */}
          <div data-tour="mov-lista" style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
          }}>
          {ingresosFiltrados.length === 0 ? (
            <p style={{ padding: 16, fontSize: 13, color: 'var(--color-muted)' }}>
              {filtroCategoria
                ? t('sin_ingresos_categoria')
                : t('sin_ingresos')}
            </p>
          ) : (
            ingresosFiltrados.map((i) => (
              <div
                key={i.id}
                className="transaction-item"
                style={{
                  padding: 14,
                  borderBottom: '1px solid var(--color-border)',
                  display: 'grid',
                  gridTemplateColumns:
                    window.innerWidth < 768
                      ? '1fr'
                      : editingId === i.id
                        ? '1fr 220px'
                        : '1fr 140px 90px',
                  alignItems: 'center',
                  gap: 12
                }}
              >
                {/* INFO */}
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {i.category || t('sin_categoria')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                    {i.description}
                  </div>
                </div>

                {/* MONTO / EDICIÓN */}
                <div style={{
                  width: '100%',
                  maxWidth: editingId === i.id ? '260px' : '140px',
                  minWidth: editingId === i.id ? '200px' : '140px',
                  textAlign: 'right',
                  justifySelf: 'end'
                }}>
                  {editingId === i.id ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 8,
                      width: '100%'
                    }}>
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: 90,
                          height: 34,
                          padding: '0 8px',
                          borderRadius: 6,
                          border: '1px solid var(--color-border)'
                        }}
                      />

                      <div style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={() => handleUpdate(i.id)}
                          style={{
                            background: '#22c55e',
                            border: 'none',
                            color: '#fff',
                            height: 34,
                            padding: '0 10px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            transition: '0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {t('guardar_edicion')}
                        </button>

                        <button
                          onClick={() => setEditingId(null)}
                          style={{
                            background: '#ef4444',
                            border: 'none',
                            color: '#fff',
                            height: 34,
                            padding: '0 10px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            transition: '0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {t('cancelar_edicion')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700 }}>
                          {format(i.amount)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                          {i.date}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* HOVER ACTIONS */}
                {editingId !== i.id && (
                  <div
                    className="actions"
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center'
                    }}
                  >
                    <button
                      onClick={() => {
                        setEditingId(i.id)
                        setEditValue(i.amount)
                      }}
                      style={{
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-primary)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: 14,
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-surface)'
                        e.currentTarget.style.transform = 'scale(1.05)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-bg)'
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                    >
                      ✏️
                    </button>

                    <button
                      onClick={() => handleDelete(i.id)}
                      style={{
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.25)',
                        color: '#f87171',
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: 14,
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239,68,68,0.2)'
                        e.currentTarget.style.transform = 'scale(1.05)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239,68,68,0.1)'
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>  {/* ← cierre de la lista */}

        {/* Modal confirmar eliminación */}
        {confirmDeleteId && (
          <div style={{
            position: 'fixed', top: 0, left: 0,
            width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 200, padding: 16, boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#111', padding: 24, borderRadius: 20,
              width: '100%', maxWidth: 320,
              display: 'flex', flexDirection: 'column', gap: 12,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 36 }}>🗑️</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
                {t('eliminar_registro_titulo')}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {t('eliminar_registro_desc')}
              </div>
              <button
                onClick={confirmarDelete}
                style={{
                  padding: '12px', borderRadius: 10,
                  background: '#ef4444', color: '#fff',
                  border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: 14
                }}
              >
                {t('si_eliminar')}
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '12px', borderRadius: 10,
                  background: '#333', color: '#fff',
                  border: 'none', cursor: 'pointer', fontWeight: 600
                }}
              >
                {t('cancelar')}
              </button>
            </div>
          </div>
        )}

      </main>
      <PlantillasRapidas type="income" categorias={CATEGORIAS_INGRESO} onAdded={fetchIngresos} />
    </div>
  )
}